import {
    Ban,
    CalendarCheck,
    ExternalLink,
    PackageCheck,
    Plus,
    RefreshCw,
    Send,
    ShoppingCart,
    Sparkles,
    X,
} from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';

import type { DataViewActionSection, DataViewPopupProps } from 'components/DataView';
import { SupplyOrderState, TotadminPermission } from 'data/__generated__';

import { CreateSupplyOrderPopup } from '../Popups/CreateSupplyOrderPopup';
import { GenerateSupplyOrdersNeededPopup } from '../Popups/GenerateSupplyOrdersNeededPopup';
import { MarkAsAbortedPopup } from '../Popups/MarkAsAbortedPopup';
import { MarkAsConfirmedPopup } from '../Popups/MarkAsConfirmedPopup';
import { MarkAsOrderedPopup } from '../Popups/MarkAsOrderedPopup';
import { MarkAsReadyPopup } from '../Popups/MarkAsReadyPopup';
import { MarkAsReceivedPopup } from '../Popups/MarkAsReceivedPopup';
import { RelaunchPassSupplyOrdersToOrderedPopup } from '../Popups/RelaunchPassSupplyOrdersToOrderedPopup';

import type { SupplyOrderRow } from './supplyOrderViews';

/** Une transition ne s'offre que si TOUS les achats sélectionnés peuvent la prendre. */
const whenAllAre =
    (...states: SupplyOrderState[]) =>
    (selected: SupplyOrderRow[]) =>
        selected.length > 0 && selected.every(({ state }) => states.includes(state));

/** Les cinq popups « Marquer comme » partagent la même signature. */
const markAsPopupProps = ({
    rows,
    selectedKeys,
    setSelectedKeys,
    isOpen,
    setIsOpen,
}: DataViewPopupProps<SupplyOrderRow>) => ({
    supplyOrders: rows,
    selectedSupplyOrderIds: selectedKeys,
    setSelectedSupplyOrderIds: (ids: string[]) => setSelectedKeys(ids),
    isOpen,
    setIsOpen,
});

/**
 * La barre d'actions des fiches d'achat, en config. Elle vivait en double — une copie dans le kanban,
 * une dans la timeline — avec huit booléens d'ouverture chacune ; la `DataView` porte désormais l'état
 * d'ouverture et monte la popup ouverte, quelle que soit la vue affichée.
 */
export function buildSupplyOrderActions(navigate: NavigateFunction): DataViewActionSection<SupplyOrderRow>[] {
    return [
        {
            title: 'Actions',
            actions: [
                {
                    key: 'create',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <Plus />,
                    label: 'Créer',
                    renderPopup: ({ isOpen, setIsOpen }) => (
                        <CreateSupplyOrderPopup isOpen={isOpen} setIsOpen={setIsOpen} />
                    ),
                },
                {
                    key: 'generate-needed',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <Sparkles />,
                    label: 'Générer les achats nécessaires',
                    renderPopup: ({ isOpen, setIsOpen }) => (
                        <GenerateSupplyOrdersNeededPopup isOpen={isOpen} setIsOpen={setIsOpen} />
                    ),
                },
                {
                    key: 'relaunch-pass-to-ordered',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <RefreshCw />,
                    label: 'Relancer le passage auto en "Commandé"',
                    renderPopup: ({ isOpen, setIsOpen }) => (
                        <RelaunchPassSupplyOrdersToOrderedPopup isOpen={isOpen} setIsOpen={setIsOpen} />
                    ),
                },
                {
                    key: 'open',
                    icon: <ExternalLink />,
                    label: 'Ouvrir',
                    visibleWhen: (selected) => selected.length === 1,
                    onClick: ([supplyOrder]) => navigate(`/supply-order/${supplyOrder._id}`),
                },
                {
                    key: 'clear-selection',
                    icon: <X />,
                    label: 'Tout désélectionner',
                    visibleWhen: (selected) => selected.length > 0,
                    onClick: (_selected, { setSelectedKeys }) => setSelectedKeys([]),
                },
            ],
        },
        {
            title: 'Marquer comme',
            actions: [
                {
                    key: 'ready',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <Send />,
                    label: 'à envoyer',
                    visibleWhen: whenAllAre(SupplyOrderState.Created),
                    renderPopup: (props) => <MarkAsReadyPopup {...markAsPopupProps(props)} />,
                },
                {
                    key: 'ordered',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <ShoppingCart />,
                    label: 'commandée(s)',
                    visibleWhen: whenAllAre(SupplyOrderState.Ready),
                    renderPopup: (props) => <MarkAsOrderedPopup {...markAsPopupProps(props)} />,
                },
                {
                    key: 'confirmed',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <CalendarCheck />,
                    label: 'confirmée(s)',
                    visibleWhen: whenAllAre(SupplyOrderState.Ordered),
                    renderPopup: (props) => <MarkAsConfirmedPopup {...markAsPopupProps(props)} />,
                },
                {
                    key: 'received',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <PackageCheck />,
                    label: 'reçues(s)',
                    visibleWhen: whenAllAre(SupplyOrderState.Ordered, SupplyOrderState.Confirmed),
                    renderPopup: (props) => <MarkAsReceivedPopup {...markAsPopupProps(props)} />,
                },
                {
                    key: 'aborted',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    icon: <Ban />,
                    label: 'annulée(s)',
                    visibleWhen: whenAllAre(
                        SupplyOrderState.Created,
                        SupplyOrderState.Ready,
                        SupplyOrderState.Ordered,
                        SupplyOrderState.Confirmed,
                        SupplyOrderState.OnHold,
                        SupplyOrderState.Aborted,
                    ),
                    renderPopup: (props) => <MarkAsAbortedPopup {...markAsPopupProps(props)} />,
                },
            ],
        },
    ];
}
